import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Calendar,
  BarChart3,
  Trophy,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { formatDateForInput, createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'

interface HourlyData {
  hour: number
  // 1 when this clock hour actually falls on the calendar day *after* the
  // selected date — only possible for restaurants whose business day crosses
  // midnight (e.g. open 10am, close 4am), where a 1am sale still belongs to
  // that business day but lands on the next calendar date.
  dayOffset?: number
  online: { revenue: number; orders: number }
  parcel: { revenue: number; orders: number }
  takeaway: { revenue: number; orders: number }
  dineIn: { revenue: number; orders: number }
  total: { revenue: number; orders: number }
}

interface OrderTypeSummary {
  revenue: number
  orders: number
  percentage: number
}

interface PeakHourItem {
  name: string
  category: string
  quantity: number
  revenue: number
  orderType: string
}

interface HourlyItemBreakdown {
  hour: number
  dayOffset?: number
  items: {
    online: PeakHourItem[]
    parcel: PeakHourItem[]
    takeaway: PeakHourItem[]
    dineIn: PeakHourItem[]
  }
}

interface HourlyDistributionData {
  hourlyDistribution: HourlyData[]
  hourlyItemBreakdown: HourlyItemBreakdown[]
  summary: {
    totalRevenue: number
    totalOrders: number
    online: OrderTypeSummary
    parcel: OrderTypeSummary
    takeaway: OrderTypeSummary
    dineIn: OrderTypeSummary
  }
  peakHour: {
    hour: number
    dayOffset?: number
    revenue: number
    orders: number
  }
  peakHours: { hour: number; dayOffset?: number }[]
  peakHourTopItems: {
    online: PeakHourItem[]
    parcel: PeakHourItem[]
    takeaway: PeakHourItem[]
    dineIn: PeakHourItem[]
  }
}

export default function HourlyDistributionScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [data, setData] = useState<HourlyDistributionData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [expandedHour, setExpandedHour] = useState<number | null>(null)
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)

  const loadData = async () => {
    if (!currentRestaurant?._id) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.getHourlyDistribution(currentRestaurant._id, dateRange)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.message || 'Failed to load hourly distribution data')
      }
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load data'
        : 'Failed to load data'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadData()
    }
  }, [currentRestaurant?._id])

  const handleDateChange = (value: string) => {
    const newDate = parseDate(value)
    setDateRange(createDateRange(newDate, newDate))
  }

  const formatHour = (hour: number, dayOffset?: number) => {
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    const label = `${displayHour}:00 ${period}`
    return dayOffset === 1 ? `${label} (next day)` : label
  }

  const getOrderTypeColor = (type: 'online' | 'parcel' | 'takeaway' | 'dineIn') => {
    const colors = {
      online: 'bg-blue-500',
      parcel: 'bg-green-500',
      takeaway: 'bg-yellow-500',
      dineIn: 'bg-purple-500'
    }
    return colors[type]
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading hourly distribution...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="outline"
              size="sm"
              className="ml-2"
              onClick={loadData}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No data available for the selected period.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const maxRevenue = Math.max(...data.hourlyDistribution.map(h => h.total.revenue))

  return (
    <div className="space-y-3 sm:space-y-4 pb-20 sm:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Hourly Distribution</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Revenue by hour & type
          </p>
        </div>
        <Button onClick={loadData} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Date Selector - Desktop Only */}
      <div className="hidden md:flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 flex items-center gap-2">
          <Label htmlFor="hourly-date-desktop" className="sr-only">
            Date
          </Label>
          <Input
            id="hourly-date-desktop"
            type="date"
            value={formatDateForInput(dateRange.from)}
            onChange={(e) => handleDateChange(e.target.value)}
            className="h-10 w-full sm:w-auto"
          />
        </div>
        <Button
          onClick={loadData}
          className="sm:w-auto flex items-center justify-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards - Mobile Optimized */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="overflow-hidden">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('online')}`} />
              <span className="text-xs sm:text-sm text-muted-foreground">
                {data.summary.online.percentage.toFixed(0)}%
              </span>
            </div>
            <div className="text-base sm:text-2xl font-bold truncate">{formatCurrency(data.summary.online.revenue)}</div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Online
            </p>
            <p className="text-xs text-muted-foreground">
              {data.summary.online.orders} orders
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('parcel')}`} />
              <span className="text-xs sm:text-sm text-muted-foreground">
                {data.summary.parcel.percentage.toFixed(0)}%
              </span>
            </div>
            <div className="text-base sm:text-2xl font-bold truncate">{formatCurrency(data.summary.parcel.revenue)}</div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Parcel
            </p>
            <p className="text-xs text-muted-foreground">
              {data.summary.parcel.orders} orders
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('takeaway')}`} />
              <span className="text-xs sm:text-sm text-muted-foreground">
                {data.summary.takeaway.percentage.toFixed(0)}%
              </span>
            </div>
            <div className="text-base sm:text-2xl font-bold truncate">{formatCurrency(data.summary.takeaway.revenue)}</div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Takeaway
            </p>
            <p className="text-xs text-muted-foreground">
              {data.summary.takeaway.orders} orders
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('dineIn')}`} />
              <span className="text-xs sm:text-sm text-muted-foreground">
                {data.summary.dineIn.percentage.toFixed(0)}%
              </span>
            </div>
            <div className="text-base sm:text-2xl font-bold truncate">{formatCurrency(data.summary.dineIn.revenue)}</div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Dine-In
            </p>
            <p className="text-xs text-muted-foreground">
              {data.summary.dineIn.orders} orders
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Peak Hour Info - Mobile Optimized */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
            Peak Hours
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950 dark:to-orange-950 p-3 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs sm:text-sm font-medium">Primary Peak:</span>
              <div className="flex items-center gap-2">
                <Badge variant="default" className="text-xs sm:text-sm">
                  {formatHour(data.peakHour.hour, data.peakHour.dayOffset)}
                </Badge>
                <span className="text-xs sm:text-sm font-bold">{formatCurrency(data.peakHour.revenue)}</span>
              </div>
            </div>
          </div>
          <div>
            <span className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 block">Top 3 Hours:</span>
            <div className="flex gap-2 flex-wrap">
              {data.peakHours.map(({ hour, dayOffset }) => (
                <Badge key={hour} variant="outline" className="text-xs sm:text-sm">
                  {formatHour(hour, dayOffset)}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hourly Chart - Mobile Optimized */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
            Hourly Revenue
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">Tap hour to view details</CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <div className="space-y-2">
            {data.hourlyDistribution.filter(h => h.total.revenue > 0).map((hourData) => (
              <div key={hourData.hour} className="space-y-1">
                <div className="flex items-center justify-between text-xs sm:text-sm px-2">
                  <span className="font-medium w-16 sm:w-20">{formatHour(hourData.hour, hourData.dayOffset)}</span>
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {formatCurrency(hourData.total.revenue)}
                  </span>
                </div>
                <div className="relative h-4 sm:h-6 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  {hourData.total.revenue > 0 && (
                    <>
                      <div
                        className={`absolute left-0 top-0 h-full ${getOrderTypeColor('online')} transition-all`}
                        style={{
                          width: `${(hourData.online.revenue / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`
                        }}
                      />
                      <div
                        className={`absolute left-0 top-0 h-full ${getOrderTypeColor('parcel')} transition-all`}
                        style={{
                          left: `${(hourData.online.revenue / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`,
                          width: `${(hourData.parcel.revenue / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`
                        }}
                      />
                      <div
                        className={`absolute left-0 top-0 h-full ${getOrderTypeColor('takeaway')} transition-all`}
                        style={{
                          left: `${((hourData.online.revenue + hourData.parcel.revenue) / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`,
                          width: `${(hourData.takeaway.revenue / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`
                        }}
                      />
                      <div
                        className={`absolute left-0 top-0 h-full ${getOrderTypeColor('dineIn')} transition-all`}
                        style={{
                          left: `${((hourData.online.revenue + hourData.parcel.revenue + hourData.takeaway.revenue) / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`,
                          width: `${(hourData.dineIn.revenue / hourData.total.revenue) * (hourData.total.revenue / maxRevenue) * 100}%`
                        }}
                      />
                    </>
                  )}
                </div>
                <div className="text-xs text-muted-foreground px-2">
                  {hourData.total.orders} orders
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Hourly Item Breakdown - Mobile Optimized with Collapse */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
            Items by Hour
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">Top items per order type</CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <div className="space-y-2">
            {data.hourlyItemBreakdown
              .filter(hourData =>
                hourData.items.online.length > 0 ||
                hourData.items.parcel.length > 0 ||
                hourData.items.takeaway.length > 0 ||
                hourData.items.dineIn.length > 0
              )
              .map((hourData) => (
                <div key={hourData.hour} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedHour(expandedHour === hourData.hour ? null : hourData.hour)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="font-semibold text-sm sm:text-base">{formatHour(hourData.hour, hourData.dayOffset)}</span>
                    {expandedHour === hourData.hour ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                  {expandedHour === hourData.hour && (
                    <div className="p-3 pt-0 border-t">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                    {/* Online */}
                    {hourData.items.online.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <div className={`h-2 w-2 rounded-full ${getOrderTypeColor('online')}`} />
                          <span className="text-xs font-medium">Online</span>
                        </div>
                        <div className="space-y-1">
                          {hourData.items.online.map((item, idx) => (
                            <div key={idx} className="text-xs bg-blue-50 dark:bg-blue-950/20 p-2 rounded">
                              <div className="font-medium truncate">{item.name}</div>
                              <div className="text-muted-foreground">{item.quantity} sold</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Parcel */}
                    {hourData.items.parcel.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <div className={`h-2 w-2 rounded-full ${getOrderTypeColor('parcel')}`} />
                          <span className="text-xs font-medium">Parcel</span>
                        </div>
                        <div className="space-y-1">
                          {hourData.items.parcel.map((item, idx) => (
                            <div key={idx} className="text-xs bg-green-50 dark:bg-green-950/20 p-2 rounded">
                              <div className="font-medium truncate">{item.name}</div>
                              <div className="text-muted-foreground">{item.quantity} sold</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Takeaway */}
                    {hourData.items.takeaway.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <div className={`h-2 w-2 rounded-full ${getOrderTypeColor('takeaway')}`} />
                          <span className="text-xs font-medium">Takeaway</span>
                        </div>
                        <div className="space-y-1">
                          {hourData.items.takeaway.map((item, idx) => (
                            <div key={idx} className="text-xs bg-yellow-50 dark:bg-yellow-950/20 p-2 rounded">
                              <div className="font-medium truncate">{item.name}</div>
                              <div className="text-muted-foreground">{item.quantity} sold</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dine-In */}
                    {hourData.items.dineIn.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <div className={`h-2 w-2 rounded-full ${getOrderTypeColor('dineIn')}`} />
                          <span className="text-xs font-medium">Dine-In</span>
                        </div>
                        <div className="space-y-1">
                          {hourData.items.dineIn.map((item, idx) => (
                            <div key={idx} className="text-xs bg-purple-50 dark:bg-purple-950/20 p-2 rounded">
                              <div className="font-medium truncate">{item.name}</div>
                              <div className="text-muted-foreground">{item.quantity} sold</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Peak Hour Top Items - Mobile Optimized */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
            Peak Hour Top Items
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            During {data.peakHours.map(({ hour, dayOffset }) => formatHour(hour, dayOffset)).join(', ')}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <div className="space-y-4">
            {/* Online Orders */}
            {data.peakHourTopItems.online.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-2">
                  <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('online')}`} />
                  <h3 className="font-semibold text-base">Online</h3>
                </div>
                <div className="space-y-1.5">
                  {data.peakHourTopItems.online.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2.5 sm:p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="font-medium text-sm sm:text-base truncate">{item.name}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-sm sm:text-base">{item.quantity} sold</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">{formatCurrency(item.revenue)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Parcel Orders */}
            {data.peakHourTopItems.parcel.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-2">
                  <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('parcel')}`} />
                  <h3 className="font-semibold text-base">Parcel</h3>
                </div>
                <div className="space-y-1.5">
                  {data.peakHourTopItems.parcel.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2.5 sm:p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="font-medium text-sm sm:text-base truncate">{item.name}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-sm sm:text-base">{item.quantity} sold</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">{formatCurrency(item.revenue)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Takeaway Orders */}
            {data.peakHourTopItems.takeaway.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-2">
                  <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('takeaway')}`} />
                  <h3 className="font-semibold text-base">Takeaway</h3>
                </div>
                <div className="space-y-1.5">
                  {data.peakHourTopItems.takeaway.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2.5 sm:p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="font-medium text-sm sm:text-base truncate">{item.name}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-sm sm:text-base">{item.quantity} sold</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">{formatCurrency(item.revenue)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dine-In Orders */}
            {data.peakHourTopItems.dineIn.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-2">
                  <div className={`h-2 w-2 sm:h-3 sm:w-3 rounded-full ${getOrderTypeColor('dineIn')}`} />
                  <h3 className="font-semibold text-base">Dine-In</h3>
                </div>
                <div className="space-y-1.5">
                  {data.peakHourTopItems.dineIn.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2.5 sm:p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="font-medium text-sm sm:text-base truncate">{item.name}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-semibold text-sm sm:text-base">{item.quantity} sold</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">{formatCurrency(item.revenue)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!data.peakHourTopItems.online.length &&
             !data.peakHourTopItems.parcel.length &&
             !data.peakHourTopItems.takeaway.length &&
             !data.peakHourTopItems.dineIn.length && (
              <p className="text-center text-muted-foreground py-4 text-sm">No items sold during peak hours</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Floating Date Filter */}
      <div className="fixed bottom-40 right-4 z-50 md:hidden">
        <div className="flex flex-col items-end gap-2">
          <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-muted-foreground border shadow-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              <span>{dateRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
          </div>

          <Dialog open={isMobileFilterOpen} onOpenChange={setIsMobileFilterOpen}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                className="h-14 w-14 rounded-full shadow-lg bg-yellow-500 text-black hover:bg-yellow-600 flex items-center justify-center border-2 border-white"
                aria-label="Filter by date"
              >
                <Calendar
                  className="w-6 h-6"
                  style={{ color: 'black', stroke: 'black', fill: 'none' }}
                />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px] w-[calc(100vw-2rem)]">
              <DialogHeader className="space-y-3">
                <DialogTitle className="flex items-center space-x-2 text-lg">
                  <Calendar className="h-5 w-5" />
                  <span>Hourly Distribution Date</span>
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Select the date for your hourly distribution report
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Label htmlFor="hourly-date-mobile" className="text-base font-medium">
                  Date
                </Label>
                <Input
                  id="hourly-date-mobile"
                  type="date"
                  value={formatDateForInput(dateRange.from)}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="w-full h-12 text-base block"
                />
              </div>
              <DialogFooter className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="w-full sm:w-auto h-12 text-base"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    loadData()
                    setIsMobileFilterOpen(false)
                  }}
                  className="w-full sm:w-auto h-12 text-base"
                >
                  Apply
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
